/** Achievements — lightweight gamification with +5 days each 10 achievements. */

import { motion } from "framer-motion";
import { ArrowLeft, CalendarPlus, CheckCircle2, Gift, Lock, Sparkles, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card } from "@/design";
import { useAchievements, useCheckAchievements, type AchievementItem } from "@/api/growth";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export function AchievementsPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useAchievements();
  const check = useCheckAchievements();
  const unlocked = data?.unlocked_count ?? 0;
  const total = data?.total_count ?? 0;
  const percent = total > 0 ? Math.round((unlocked / total) * 100) : 0;

  const handleCheck = async () => {
    try {
      const res = await check.mutateAsync();
      haptic.notify(res.reward_days_granted ? "success" : "warning");
      if (res.reward_days_granted) {
        toast.success(`Начислено +${res.reward_days_granted} дней за ачивки`);
      } else if (res.new_count) {
        toast.success(`Открыто новых ачивок: ${res.new_count}`);
      } else {
        toast.message("Новых ачивок пока нет");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось проверить ачивки");
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate(-1);
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Ачивки</div>
        <button
          onClick={handleCheck}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Проверить"
        >
          <Sparkles className={cn("size-4", check.isPending && "animate-pulse text-accent")} />
        </button>
      </div>

      <Card padded={false} className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/25 via-transparent to-transparent" aria-hidden />
        <div className="relative p-6 space-y-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium text-text-muted">
            <Trophy className="size-4 text-accent" />
            Прогресс
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[44px] leading-none font-semibold tabular-nums tracking-tight">{unlocked}</span>
            <span className="text-text-muted text-sm">из {total}</span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
            <div className="h-full rounded-full bg-gradient-accent" style={{ width: `${percent}%` }} />
          </div>
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-3 flex items-start gap-3">
            <div className="size-9 rounded-xl bg-accent-soft text-accent grid place-items-center shrink-0">
              <CalendarPlus className="size-4" />
            </div>
            <div>
              <div className="text-[13px] font-medium">Награда каждые 10 ачивок</div>
              <div className="text-[11px] text-text-muted leading-relaxed mt-0.5">
                За каждые 10 открытых ачивок начисляем <b>+5 дней</b> подписки. До следующей награды: {data?.next_reward_in ?? 10}.
              </div>
            </div>
          </div>
          <Button fullWidth leftIcon={<Gift />} onClick={handleCheck} loading={check.isPending}>
            Проверить новые ачивки
          </Button>
        </div>
      </Card>

      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1">
          Список ачивок
        </div>
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04] overflow-hidden">
          {(data?.items ?? []).map((item, index) => <AchievementRow key={item.code} item={item} index={index} />)}
          {!data && <div className="px-4 py-10 text-center text-xs text-text-muted">{isLoading ? "Загрузка…" : "Нет данных"}</div>}
        </div>
      </div>
    </div>
  );
}

function AchievementRow({ item, index }: { item: AchievementItem; index: number }) {
  const unlocked = item.status === "unlocked";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.018, duration: 0.22 }}
      className="px-4 py-3.5 flex items-start gap-3"
    >
      <div className={cn("size-9 rounded-xl grid place-items-center shrink-0", unlocked ? "bg-success/15 text-success" : "bg-white/[0.04] text-text-dim")}>
        {unlocked ? <CheckCircle2 className="size-4" /> : <Lock className="size-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn("text-[13px] font-medium", !unlocked && "text-text-muted")}>{item.title}</div>
        <div className="text-[11px] text-text-muted leading-relaxed mt-0.5">{item.description}</div>
      </div>
    </motion.div>
  );
}
